<template>
  <!-- The camera is fixed: the chamber is a framed shot, and a click throws. -->
  <canvas ref="canvas" class="dice-canvas"></canvas>
</template>

<script>
import { markRaw } from 'vue'
import D20Service from '@/services/dice/d20Service.js'
import RollStateService from '@/services/dice/rollStateService.js'
import D20TextureService from '@/services/dice/d20TextureService.js'

export default {
  name: 'DiceCanvas',

  props: {
    preloadedTexture: {
      type: File,
      default: null
    },
    /** Chosen once per mount; the modal remounts this canvas when it changes. */
    theme: {
      type: String,
      default: 'default'
    }
  },

  emits: ['ready', 'error', 'roll-complete', 'state-change', 'number-change'],

  data() {
    return {
      d20Service: null,
      rollStateServices: [], // Array of roll state services for multiple dice
      textureService: null,
      isInitialized: false,
      animationId: null,
      lastTime: 0,

      // Multi-dice support
      diceCount: 1,
      resultMode: 'best',
      activeRolls: [], // Track active rolling states
      pendingResults: [], // Store results as they complete
      faceTweens: [] // Quick re-orientations when modifiers change the result
    }
  },

  async mounted() {
    try {
      await this.initializeServices()

      // Apply preloaded texture if provided before creating dice
      if (this.preloadedTexture) {
        await this.d20Service.setUserTexture(this.preloadedTexture)
      }

      await this.setupCanvas()
      this.startAnimation()
      this.$emit('ready')
    } catch (error) {
      console.error('Failed to initialize dice canvas:', error)
      this.$emit('error', error)
    }
  },

  beforeUnmount() {
    this.cleanup()
  },

  methods: {
    async initializeServices() {
      // Initialize services with markRaw to prevent Vue reactivity
      this.d20Service = markRaw(new D20Service(this.theme))
      this.textureService = markRaw(new D20TextureService())

      // Initialize roll state services for potential multiple dice
      this.createRollStateServices(2) // Initialize for up to 2 dice
    },

    createRollStateServices(maxDice) {
      // Clear existing services
      this.rollStateServices.forEach(service => service.dispose())
      this.rollStateServices = []

      // Create roll state services for each potential dice
      for (let i = 0; i < maxDice; i++) {
        const rollStateService = markRaw(new RollStateService())
        rollStateService.setGeometry(this.d20Service.getPhysicsGeometry())

        // Set initial position for this specific dice based on its index
        this.initializeRollStatePosition(rollStateService, i)

        rollStateService.setCallbacks({
          onStateChange: (state) => this.handleStateChange(state, i),
          onRollComplete: (result) => this.handleRollComplete(result, i),
          onNumberChange: (number) => this.handleNumberChange(number, i)
        })
        this.rollStateServices.push(rollStateService)
      }
    },

    initializeRollStatePosition(rollStateService) {
      // Dice are kept apart by the simulation's arena divider, not by offsetting
      // their rest positions, so every die simulates in true ring coordinates.
      rollStateService.setRestPosition(0, 0.76, 0)
    },

    /** -1 and +1 give each die its own half of the ring; 0 is the whole ring. */
    arenaHalfFor(diceIndex) {
      if (this.diceCount < 2) return 0
      return diceIndex === 0 ? -1 : 1
    },

    async setupCanvas() {
      const canvas = this.$refs.canvas
      const parent = canvas.parentElement

      // Set canvas size
      const width = parent.clientWidth || 800
      const height = parent.clientHeight || 600

      canvas.width = width
      canvas.height = height
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`

      // Initialize D20 service
      const success = await this.d20Service.initializeScene(canvas, width, height)
      if (!success) {
        throw new Error('Failed to initialize 3D scene')
      }

      // Create initial dice
      this.d20Service.createDice()
      this.isInitialized = true
    },

    startAnimation() {
      const animate = (currentTime) => {
        if (!this.isInitialized) return

        const deltaTime = (currentTime - this.lastTime) / 1000
        this.lastTime = currentTime

        // Update roll states for all active dice
        this.rollStateServices.forEach((rollStateService, index) => {
          if (rollStateService.isCurrentlyRolling() && index < this.diceCount) {
            const state = rollStateService.update(deltaTime, this.d20Service.faceNumbers)

            this.d20Service.updateDiceQuaternion(state.quaternion, index)
            this.d20Service.updateDicePosition(state.position, index)
          }
        })

        this.updateFaceTweens(deltaTime)

        // Render scene
        this.d20Service.render(deltaTime)

        this.animationId = requestAnimationFrame(animate)
      }

      this.animationId = requestAnimationFrame(animate)
    },

    async rollToTarget(targetNumber, options = {}) {
      if (!this.isInitialized || this.isAnyDiceRolling()) {
        return null
      }

      if (this.diceCount === 1) {
        // Single dice roll
        this.d20Service.highlightFace(-1, 0)
        return new Promise((resolve) => {
          this.pendingRollResolve = resolve
          this.rollStateServices[0].startRoll(targetNumber, true, options)
        })
      } else {
        // Multiple dice roll - each dice gets the same hasTarget for now
        // For different targets per dice, use rollToTargets() method
        return this.rollMultipleDice([targetNumber, targetNumber], true)
      }
    },

    async rollToTargets(targetNumbers, options = {}) {
      if (!this.isInitialized || this.isAnyDiceRolling()) {
        return null
      }

      if (!Array.isArray(targetNumbers)) {
        return this.rollToTarget(targetNumbers, options)
      }

      // Ensure we have enough targets for all dice
      const targets = [...targetNumbers]
      while (targets.length < this.diceCount) {
        targets.push(targets[targets.length - 1] || 20)
      }

      return this.rollMultipleDice(targets.slice(0, this.diceCount), true, options)
    },

    isAnyDiceRolling() {
      return this.rollStateServices.some(service => service.isCurrentlyRolling())
    },

    /**
     * Turns a settled die to show a different number without throwing it again.
     * Modifiers adjust an already-decided roll, so re-rolling would misrepresent
     * what happened — and would cost a full throw per card.
     */
    showFace(number, diceIndex = 0, duration = 0.55) {
      if (!this.isInitialized) return Promise.resolve(null)

      const dice = this.d20Service.diceArray[diceIndex]
      const faceIndex = this.d20Service.faceNumbers.indexOf(number)
      if (!dice || faceIndex === -1) return Promise.resolve(null)

      this.d20Service.highlightFace(null, diceIndex)

      return new Promise((resolve) => {
        this.faceTweens = this.faceTweens.filter(tween => tween.diceIndex !== diceIndex)
        this.faceTweens.push({
          diceIndex,
          number,
          elapsed: 0,
          duration,
          from: dice.quaternion.clone(),
          to: this.d20Service.calculateTargetQuaternion(faceIndex),
          resolve
        })
      })
    },

    updateFaceTweens(deltaTime) {
      if (!this.faceTweens.length) return

      this.faceTweens = this.faceTweens.filter((tween) => {
        tween.elapsed += deltaTime
        const t = Math.min(1, tween.elapsed / tween.duration)
        // Ease out so the die swings round and settles rather than snapping.
        const eased = 1 - Math.pow(1 - t, 3)

        const quaternion = tween.from.clone().slerp(tween.to, eased)
        this.d20Service.updateDiceQuaternion(quaternion, tween.diceIndex)

        if (t < 1) return true

        this.d20Service.highlightFace(tween.number, tween.diceIndex)
        this.$emit('number-change', tween.number)
        tween.resolve(tween.number)
        return false
      })
    },

    async rollRandom() {
      if (!this.isInitialized || this.isAnyDiceRolling()) {
        return null
      }

      if (this.diceCount === 1) {
        // Single dice random roll - use deterministic animation with random hasTarget
        const randomTarget = Math.floor(Math.random() * 20) + 1
        return new Promise((resolve) => {
          this.pendingRollResolve = resolve
          this.rollStateServices[0].startRoll(randomTarget, true)
        })
      } else {
        // Multiple dice random roll - generate random targets for each dice
        const randomTargets = []
        for (let i = 0; i < this.diceCount; i++) {
          randomTargets.push(Math.floor(Math.random() * 20) + 1)
        }
        return this.rollMultipleDice(randomTargets, true)
      }
    },

    async rollMultipleDice(targetNumbers = null, deterministic = false, options = {}) {
      this.d20Service.clearHighlights()

      return new Promise((resolve) => {
        this.pendingResults = []
        this.pendingRollResolve = resolve

        // Start all dice rolling simultaneously
        for (let i = 0; i < this.diceCount; i++) {
          let diceTarget

          if (Array.isArray(targetNumbers)) {
            // Individual targets for each dice
            diceTarget = targetNumbers[i] || (Math.floor(Math.random() * 20) + 1)
          } else if (targetNumbers !== null) {
            // Same hasTarget for all dice
            diceTarget = targetNumbers
          } else {
            // Random hasTarget for each dice
            diceTarget = Math.floor(Math.random() * 20) + 1
          }

          this.rollStateServices[i].startRoll(diceTarget, deterministic, {
            ...options,
            seed: options.seed ? `${options.seed}:${i}` : undefined,
            half: this.arenaHalfFor(i)
          })
        }
      })
    },

    calculateMultiDiceResult(results) {
      const numbers = results.map(r => r.number)
      let finalNumber
      let resultType = 'multiple'

      switch (this.resultMode) {
        case 'best':
          finalNumber = Math.max(...numbers)
          resultType = 'best'
          break
        case 'worst':
          finalNumber = Math.min(...numbers)
          resultType = 'worst'
          break
        case 'both':
          finalNumber = numbers
          resultType = 'both'
          break
        default:
          finalNumber = Math.max(...numbers)
          resultType = 'best'
      }

      return {
        number: finalNumber,
        numbers: numbers,
        resultType: resultType,
        rollTime: Math.max(...results.map(r => r.rollTime)),
        isDeterministic: results[0].isDeterministic,
        targetNumber: results[0].targetNumber,
        diceCount: this.diceCount
      }
    },

    async setUserTexture(file) {
      if (!this.d20Service) return false

      const success = await this.d20Service.setUserTexture(file)
      if (success && this.isInitialized) {
        // Rebuild dice with new texture
        this.d20Service.createDice()
      }
      return success
    },

    resetCamera() {
      if (this.d20Service) {
        this.d20Service.setCameraView('angle')
      }
    },

    setCameraView(viewType) {
      if (this.d20Service) {
        this.d20Service.setCameraView(viewType)
      }
    },

    toggleWireframe() {
      if (this.d20Service) {
        return this.d20Service.toggleWireframe()
      }
      return false
    },

    // Material and shader control methods
    setMaterialType(materialType) {
      if (this.d20Service) {
        return this.d20Service.setMaterialType(materialType)
      }
      return false
    },

    setShaderType(shaderType) {
      if (this.d20Service) {
        return this.d20Service.setShaderType(shaderType)
      }
      return false
    },

    getMaterialTypes() {
      if (this.d20Service) {
        return this.d20Service.getMaterialTypes()
      }
      return []
    },

    getShaderTypes() {
      if (this.d20Service) {
        return this.d20Service.getShaderTypes()
      }
      return []
    },

    getCurrentMaterialType() {
      if (this.d20Service) {
        return this.d20Service.getCurrentMaterialType()
      }
      return 'plastic'
    },

    getCurrentShaderType() {
      if (this.d20Service) {
        return this.d20Service.getCurrentShaderType()
      }
      return 'none'
    },

    // Multi-dice support methods
    setDiceCount(count) {
      this.diceCount = count
      if (this.isInitialized) {
        this.updateDiceConfiguration()
      }
    },

    setResultMode(mode) {
      this.resultMode = mode
    },

    updateDiceConfiguration() {
      // Always update the dice count and recreate the scene
      this.d20Service.diceCount = this.diceCount

      if (this.diceCount === 1) {
        // Single dice mode - use original setup
        this.d20Service.createDice()
      } else if (this.diceCount === 2) {
        // Two dice mode - create two dice with appropriate positioning
        this.d20Service.createMultipleDice(2)
      }

      // Update roll state services positions for new dice count
      this.updateRollStatePositions()
    },

    updateRollStatePositions() {
      // Recalculate and update positions for all roll state services
      for (let i = 0; i < this.rollStateServices.length; i++) {
        this.initializeRollStatePosition(this.rollStateServices[i], i)
      }
    },

    // Event handlers
    handleStateChange(state, diceIndex) {
      // For single dice or first dice, emit state change
      if (diceIndex === 0) {
        this.$emit('state-change', state)
      }
    },

    handleRollComplete(result, diceIndex) {
      // Store result for this specific dice
      this.pendingResults[diceIndex] = { ...result, diceIndex }

      // Check if all dice have completed
      if (this.pendingResults.length === this.diceCount &&
          this.pendingResults.every(r => r !== undefined)) {

        if (this.diceCount === 1) {
          // Highlight in place - rebuilding the mesh here would reset the landing.
          this.d20Service.highlightFace(result.number, 0)

          this.$emit('roll-complete', result)

          if (this.pendingRollResolve) {
            this.pendingRollResolve(result)
            this.pendingRollResolve = null
          }
        } else {
          // Multiple dice - calculate final result
          const finalResult = this.calculateMultiDiceResult(this.pendingResults)

          // Highlight dice based on result mode - use ordered results by dice index
          const orderedResults = new Array(this.diceCount)
          this.pendingResults.forEach(res => {
            orderedResults[res.diceIndex] = res
          })

          this.highlightMultipleDice(orderedResults, finalResult)

          this.$emit('roll-complete', finalResult)

          if (this.pendingRollResolve) {
            this.pendingRollResolve(finalResult)
            this.pendingRollResolve = null
          }
        }

        // Clear pending results
        this.pendingResults = []
      }
    },

    /** Lights the kept die and darkens the rest. */
    setKeptDie(keptIndex, number) {
      this.d20Service.diceArray.forEach((_, index) => {
        this.d20Service.highlightFace(index === keptIndex ? number : null, index)
      })
    },

    /**
     * Only the die that counts lights up. With advantage or disadvantage the
     * other die stays dark, so it is obvious which one was kept.
     */
    highlightMultipleDice(results, finalResult) {
      const kept = Array.isArray(finalResult?.number) ? finalResult.number[0] : finalResult?.number
      let keptClaimed = false

      results.forEach((result, index) => {
        const isKept = !keptClaimed && result.number === kept
        if (isKept) keptClaimed = true
        this.d20Service.highlightFace(isKept ? result.number : null, index)
      })
    },

    handleNumberChange(number, diceIndex) {
      // For single dice or first dice, emit number change
      if (diceIndex === 0) {
        this.$emit('number-change', number)
      }
    },

    // Resize handling
    handleResize() {
      if (!this.isInitialized) return

      const canvas = this.$refs.canvas
      const parent = canvas.parentElement
      const width = parent.clientWidth || 800
      const height = parent.clientHeight || 600

      canvas.width = width
      canvas.height = height
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`

      this.d20Service.resize(width, height)
    },

    cleanup() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId)
        this.animationId = null
      }

      if (this.d20Service) {
        this.d20Service.dispose()
        this.d20Service = null
      }

      this.rollStateServices.forEach(service => {
        if (service) {
          service.dispose()
        }
      })
      this.rollStateServices = []

      if (this.textureService) {
        this.textureService.dispose()
        this.textureService = null
      }

      this.isInitialized = false
    }
  }
}
</script>

<style scoped>
.dice-canvas {
  width: 100%;
  height: 100%;
  display: block;
  background: transparent;
}
</style>